import Warning from "@/components/ui/Warning";
import { INTERNAL_DATE_FORMAT } from "@/utils/agenda";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@edusogno/primitives/avatar.js";
import Button from "@edusogno/primitives/button.js";
import LoadingButton from "@edusogno/primitives/loading-button.js";
import { ScrollArea } from "@edusogno/primitives/scroll-area.js";
import Spinner from "@edusogno/primitives/spinner.js";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@edusogno/primitives/toggle-group.js";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import isoWeek from "dayjs/plugin/isoWeek";
import weekOfYear from "dayjs/plugin/weekOfYear";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import useScheduleSpeakingClass from "@/utils/hooks/useScheduleSpeakingClass";
import routes from "@/config/routes.ts";
import { ScheduleSpeakingClassesEvent } from "@/types";
// import { useRef } from "react";

dayjs.extend(weekOfYear);
dayjs.extend(isoWeek);
dayjs.extend(advancedFormat);

const STORAGE_PATH = import.meta.env.EDS_STORAGE_URL;

const SelectMobileLessons = ({
  onConfirm,
}: {
  onConfirm: (events: ScheduleSpeakingClassesEvent[] | null) => void;
}) => {
  // const eventDetails = useRef<ScheduleSpeakingClassesEvent[] | null>(null);

  const {
    events,
    isEventsLoading,
    isBooking,
    pagination,
    selectedEvents,
    error,
    datesInWeek,
    currentWeek,
    index,
    isBookingSuccess,
    handleToggleChange,
    showEventTime,
    showTutorName,
    onBook,
    setIndex,
    remainingBookings,
    allowedBookingsPerWeek,
  } = useScheduleSpeakingClass({ onConfirm });

  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleMobileConfirmation = () => {
    if (onBook) {
      onBook();
      if (isBookingSuccess) {
        onConfirm(selectedEvents as never);
      }
    }
  };

  return (
    <div className="!flex flex-col h-[90vh]">
      <Warning
        className="!py-2 px-4 justify-start"
        text={
          <>
            {t("agenda.rememberToSelect")}{" "}
            <i className="not-italic font-semibold">
              {t("agenda.classPerWeek", { count: allowedBookingsPerWeek })}
            </i>{" "}
            {t("agenda.ofThoseListedBelow")}
          </>
        }
      />

      <div className="flex flex-col h-full  w-full">
        <div className="flex flex-col px-4 gap-4 pt-6 border-y-[1px] border-solid border-primary bg-white h-full">
          <div className="flex rounded-[9px] flex-row h-8 bg-[--primary100] items-center justify-between px-6">
            <Button
              // disabled={index == 0}
              disabled={pagination && index <= 0}
              onClick={() => setIndex((r) => r - 1)}
              variant="ghost"
              className="w-fit text-secondary"
            >
              <ChevronLeft />
            </Button>

            <p className="text-sm text-black font-medium capitalize">
              <i className="not-italic font-bold">
                {t("agenda.week")} {pagination?.currentPage} :{" "}
              </i>
              &nbsp;
              {datesInWeek.length > 0 &&
                `From ${datesInWeek[0].format("DD MMMM")} - ${datesInWeek[
                  datesInWeek.length - 1
                ].format("DD MMMM")}`}
              {/* {currentWeek.add(index, "week").startOf("week").format("DD MMMM")}
              -{currentWeek.add(index, "week").endOf("week").format("DD MMMM")} */}
            </p>

            <Button
              disabled={pagination && index >= pagination.lastPage}
              onClick={() => setIndex((r) => r + 1)}
              variant="ghost"
              className="w-fit text-secondary"
            >
              <ChevronRight />
            </Button>
          </div>

          {isEventsLoading && !events ? (
            <Spinner className="m-auto w-full h-full" />
          ) : remainingBookings === 0 ? (
            <div className="text-xs uppercase m-auto w-full text-center data-[state=on]:!bg-white !bg-none h-[16px] !opacity-100 text-black font-bold">
              <p>{t("beta-speaking-event.max_booking_reached")}</p>
            </div>
          ) : datesInWeek.map((e) =>
              currentWeek.add(index, "week").isSame(e, "week"),
            ).length == 0 ? (
            <div className="text-xs uppercase m-auto w-full text-center data-[state=on]:!bg-white !bg-none h-[16px] !opacity-100 text-black font-bold">
              <p>{t("agenda.no_events")}</p>
            </div>
          ) : (
            <ScrollArea className="min-h-o flex-[1_1_0] pl-2 pb-2">
              {datesInWeek.map((eventDate, idx) => (
                <div key={`date_${idx}`}>
                  {/* event date heading */}
                  <ToggleGroup
                    key={`date__${eventDate.format("dddd-DD-MMMM")}`}
                    type="single"
                    className="flex flex-col gap-4 h-full"
                    disabled={true}
                  >
                    <ToggleGroupItem
                      value={`${eventDate.format("dddd DD MMMM")}`}
                      className="text-sm uppercase w-full data-[state=on]:!bg-white !bg-none h-[16px] !opacity-100 text-secondary font-bold my-3"
                    >
                      <p>{dayjs(eventDate).format("dddd DD MMMM")}</p>
                    </ToggleGroupItem>
                  </ToggleGroup>

                  {/* event items */}
                  <ToggleGroup
                    type="multiple"
                    value={selectedEvents}
                    onValueChange={handleToggleChange}
                    className="flex flex-col gap-4 h-full"
                  >
                    {events &&
                      events[eventDate.format(INTERNAL_DATE_FORMAT)].map(
                        (eventItem, idx2) => (
                          <ToggleGroupItem
                            key={`event_${eventItem.id}_${idx2}`}
                            className="flex flex-row justify-between text-brand px-6 h-[50px] w-full data-[state=on]:bg-primary data-[state=on]:[--color:white]   [&_p]:text-[--color] data-[state=on]:text-white bg-[--grey200]  "
                            value={`${String(
                              eventItem?.id,
                            )}==${eventDate.format(
                              INTERNAL_DATE_FORMAT,
                            )}==${idx2}`}
                          >
                            <div className="flex flex-col items-start ">
                              <p className="text-xs text-primary font-bold">
                                {/* event start and end times */}
                                {showEventTime(eventItem)}
                              </p>

                              <p className="text-xs">
                                Topic:&nbsp;
                                <i className="not-italic  font-semibold">
                                  {eventItem?.topic}
                                </i>
                              </p>
                            </div>

                            <Avatar
                              className={"w-[28px] bg-white h-[28px]"}
                              title={showTutorName(eventItem)}
                            >
                              <AvatarImage
                                src={
                                  STORAGE_PATH +
                                  "/" +
                                  eventItem?.tutors?.profilePic
                                }
                              />
                              <AvatarFallback
                                className="text-primary"
                                title={showTutorName(eventItem)}
                              >
                                {eventItem?.tutors?.first_name?.at(0)}
                                {eventItem?.tutors?.last_name?.at(0)}
                              </AvatarFallback>
                            </Avatar>
                          </ToggleGroupItem>
                        ),
                      )}
                  </ToggleGroup>
                </div>
              ))}
            </ScrollArea>
          )}
        </div>

        <div className="flex flex-col h-[120px] items-center bg-[--primary100] pb-4 pt-2">
          {error?.invalid && (
            <p className="h-[35px] flex flex-col items-center justify-center bg-[--red200] font-semibold text-center text-xs rounded-[9px] text-[--red100] w-[65%]">
              {error?.message}
            </p>
          )}

          <div className="flex flex-row  items-center w-[65%] mx-auto justify-center mt-auto gap-6">
            <Button
              onClick={() => {
                navigate(routes.agenda, {
                  replace: true,
                });
              }}
              color="white"
              size="md"
            >
              {t("btns.exit")}
            </Button>

            <LoadingButton
              loading={isBooking}
              onClick={() => handleMobileConfirmation()}
              color="green"
              size="md"
            >
              {t("btns.confirm")}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SelectMobileLessons;
// export default React.memo(SelectMobileLessons);
